import { useState } from 'react';

import {
    Button,
    Card,
    StatusPill,
    Table,
    TableBody,
    TableCell,
    TableFootNote,
    TableHead,
    TableHeaderCell,
    TableRow,
    type Tone,
} from '@/design-system';
import type { Payment, PaymentStatus } from '@/entities/credit';
import { formatMoney } from '@/entities/invoice';
import type { Page } from '@/shared/api/envelope';
import { formatDayShort } from '@/shared/lib';

import { browser } from '../browser';
import { billingCopy } from '../copy';
import { describeBillingError } from '../errors';
import { usePlatformInvoiceLink } from '../hooks';

const STATUS_TONES: Record<PaymentStatus, Tone> = {
    open: 'neutral',
    paid: 'accent',
    failed: 'warn',
    cancelled: 'neutral',
    expired: 'neutral',
};

export interface PaymentsCardProps {
    page: Page<Payment>;
    busy?: boolean;
}

/**
 * Wat het bedrijf aan Smartkeur betaalde: elke maand van het abonnement, de
 * gekochte keuringen, en per betaalde betaling de factuur van Smartkeur als PDF.
 *
 * Een brede tabel: hij schuift opzij op een smal scherm in plaats van te knippen.
 */
export function PaymentsCard({ page, busy = false }: PaymentsCardProps) {
    const invoiceLink = usePlatformInvoiceLink();
    const [problem, setProblem] = useState<string | null>(null);
    const [blockedUrl, setBlockedUrl] = useState<string | null>(null);

    async function openInvoice(payment: Payment) {
        setProblem(null);
        setBlockedUrl(null);

        /* Het tabblad opent tijdens de klik; de link komt er daarna in. */
        const tab = browser.openBlankTab();

        try {
            const link = await invoiceLink.mutateAsync(payment.uuid);

            if (tab) {
                browser.navigateTab(tab, link.url);
            } else {
                setBlockedUrl(link.url);
            }
        } catch (error) {
            tab?.close();
            /* Ook "de PDF wordt nog gemaakt": de API zegt het in gewone taal. */
            setProblem(describeBillingError(error));
        }
    }

    return (
        <Card padding="none" as="section" aria-labelledby="payments-title">
            <h2
                id="payments-title"
                className="m-0 px-4 pt-4 pb-3 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {billingCopy.paymentsTitle}
            </h2>

            <Table
                columns="96px minmax(0,1.6fr) 120px 112px 150px"
                minWidth={640}
                label={billingCopy.paymentsTableLabel}
                aria-busy={busy || undefined}
            >
                <TableHead>
                    <TableHeaderCell>{billingCopy.columnPaymentDate}</TableHeaderCell>
                    <TableHeaderCell>{billingCopy.columnPaymentDescription}</TableHeaderCell>
                    <TableHeaderCell align="end">{billingCopy.columnPaymentAmount}</TableHeaderCell>
                    <TableHeaderCell>{billingCopy.columnPaymentStatus}</TableHeaderCell>
                    <TableHeaderCell>{billingCopy.columnPaymentInvoice}</TableHeaderCell>
                </TableHead>
                <TableBody>
                    {page.items.map((payment) => (
                        <TableRow key={payment.uuid}>
                            <TableCell mono muted>
                                {payment.paidAt || payment.createdAt
                                    ? formatDayShort(payment.paidAt ?? payment.createdAt ?? '')
                                    : '—'}
                            </TableCell>
                            <TableCell emphasis truncate>
                                {payment.description ?? '—'}
                            </TableCell>
                            <TableCell mono align="end">
                                {formatMoney(payment.amount)}
                            </TableCell>
                            <TableCell>
                                <StatusPill tone={STATUS_TONES[payment.status]} size="sm">
                                    {payment.statusLabel}
                                </StatusPill>
                            </TableCell>
                            <TableCell>
                                {payment.invoice === null ? (
                                    <span className="text-[12px] text-[var(--text-tertiary-safe)]">
                                        {billingCopy.invoiceNone}
                                    </span>
                                ) : payment.invoice.pdfReady ? (
                                    <Button
                                        variant="outline"
                                        size="md"
                                        icon="download"
                                        aria-label={billingCopy.invoicePdfLabel(
                                            payment.invoice.number,
                                        )}
                                        disabled={invoiceLink.isPending}
                                        onClick={() => void openInvoice(payment)}
                                    >
                                        {payment.invoice.number}
                                    </Button>
                                ) : (
                                    <span className="text-[12px] text-[var(--text-tertiary-safe)]">
                                        {billingCopy.invoiceRendering}
                                    </span>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {page.items.length === 0 ? (
                <TableFootNote>{billingCopy.paymentsEmpty}</TableFootNote>
            ) : null}

            {problem ? (
                <p
                    role="alert"
                    className="m-0 px-4 pb-4 text-[12px] leading-[1.4] text-[var(--danger)]"
                >
                    {problem}
                </p>
            ) : null}
            {blockedUrl ? (
                <p className="m-0 px-4 pb-4 text-[12px] leading-[1.4] text-[var(--text-secondary)]">
                    {billingCopy.pdfBlocked}{' '}
                    <a
                        href={blockedUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[var(--accent)] underline"
                    >
                        {billingCopy.openPdf}
                    </a>
                </p>
            ) : null}
        </Card>
    );
}
